/**
 * Phoenix Suite marketing pages — landing / features / pricing.
 *
 * Lives on the public-site at the root paths (/, /features, /pricing)
 * so phoenixsuite.com.au lands on the product pitch. Per-operator sites
 * stay on /:slug — we reserve the root-level paths below.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const RESERVED_SLUGS = ['', 'pricing', 'features', 'about', 'contact', 'login', 'signup', 'docs', 'blog', 'rsvp', 'privacy', 'terms', 'support', 'security'];

export function isReservedSlug(slug) {
  return RESERVED_SLUGS.includes((slug || '').toLowerCase());
}

const T = {
  bg: '#0a0d16',
  card: '#121724',
  text: '#ffffff',
  muted: '#8f9bb3',
  dim: '#5a6379',
  pri: '#5b6cf0',
  priLight: '#7c8dff',
  accent: '#22d3ee',
  ok: '#10b981',
  brd: '#1e2538',
};

// "Release Imminent" pill — used wherever a mobile-app feature appears
// on the marketing site so we don't overclaim before the App Store /
// Play Store builds are live.
function ReleaseImminentBadge() {
  return (
    <span style={{
      display: 'inline-block', marginLeft: 8, padding: '2px 8px',
      fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
      borderRadius: 20, textTransform: 'uppercase',
      background: 'rgba(34,211,238,0.15)', color: '#22d3ee',
      border: '1px solid rgba(34,211,238,0.35)', verticalAlign: 'middle',
    }}>Release Imminent</span>
  );
}

const globalCss = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: ${T.bg}; color: ${T.text}; line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }
  a { color: ${T.pri}; text-decoration: none; }
  a:hover { color: ${T.priLight}; }
  .container { max-width: 1180px; margin: 0 auto; padding: 0 24px; }
  .btn { display: inline-flex; align-items: center; gap: 8px; padding: 14px 26px;
    border-radius: 10px; font-weight: 600; font-size: 15px; cursor: pointer;
    border: none; transition: all .15s ease; }
  .btn-pri { background: ${T.pri}; color: #fff; }
  .btn-pri:hover { background: ${T.priLight}; color: #fff; transform: translateY(-1px); }
  .btn-ghost { background: transparent; color: ${T.text}; border: 1px solid ${T.brd}; }
  .btn-ghost:hover { border-color: ${T.pri}; color: ${T.pri}; }
  @media (max-width: 720px) {
    .hero-title { font-size: 38px !important; }
    .hero-subtitle { font-size: 16px !important; }
  }
`;

function Header() {
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 50, background: 'rgba(10,13,22,.85)',
      backdropFilter: 'blur(12px)', borderBottom: `1px solid ${T.brd}`,
    }}>
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, color: T.text }}>
          <img src="/logo-40.png" alt="Phoenix Suite" style={{ width: 32, height: 32, borderRadius: 7 }}
               onError={(e) => { e.target.style.display = 'none'; }} />
          <span style={{ fontSize: 18, fontWeight: 700 }}>Phoenix Suite</span>
        </Link>
        <nav style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
          <Link to="/features" style={{ color: T.muted, fontSize: 14 }}>Features</Link>
          <Link to="/pricing" style={{ color: T.muted, fontSize: 14 }}>Pricing</Link>
          <a href="https://app.phoenixsuite.com.au" style={{ color: T.muted, fontSize: 14 }}>Log in</a>
          <a href="https://app.phoenixsuite.com.au/register" className="btn btn-pri" style={{ padding: '9px 18px', fontSize: 14 }}>
            Start free trial
          </a>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer style={{ padding: '60px 0 40px', borderTop: `1px solid ${T.brd}`, marginTop: 80 }}>
      <div className="container" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 40 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <img src="/logo-40.png" alt="" style={{ width: 28, height: 28, borderRadius: 6 }}
                 onError={(e) => { e.target.style.display = 'none'; }} />
            <span style={{ fontWeight: 700 }}>Phoenix Suite</span>
          </div>
          <p style={{ color: T.muted, fontSize: 13, maxWidth: 320 }}>
            Australian-made management software for martial arts academies, gyms, and personal trainers.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 40 }}>
          <div>
            <h4 style={{ fontSize: 12, color: T.dim, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 12 }}>Product</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Link to="/features" style={{ color: T.muted, fontSize: 13 }}>Features</Link>
              <Link to="/pricing" style={{ color: T.muted, fontSize: 13 }}>Pricing</Link>
              <a href="https://app.phoenixsuite.com.au/register" style={{ color: T.muted, fontSize: 13 }}>Start free trial</a>
            </div>
          </div>
          <div>
            <h4 style={{ fontSize: 12, color: T.dim, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 12 }}>Company</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Link to="/support" style={{ color: T.muted, fontSize: 13 }}>Support</Link>
              <a href="mailto:hello@phoenixsuite.com.au" style={{ color: T.muted, fontSize: 13 }}>Contact</a>
              <a href="https://app.phoenixsuite.com.au" style={{ color: T.muted, fontSize: 13 }}>Log in</a>
            </div>
          </div>
          <div>
            <h4 style={{ fontSize: 12, color: T.dim, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 12 }}>Legal</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Link to="/privacy" style={{ color: T.muted, fontSize: 13 }}>Privacy</Link>
              <Link to="/terms" style={{ color: T.muted, fontSize: 13 }}>Terms</Link>
              <Link to="/security" style={{ color: T.muted, fontSize: 13 }}>Security</Link>
            </div>
          </div>
        </div>
      </div>
      <div className="container" style={{ paddingTop: 40, color: T.dim, fontSize: 12 }}>
        © {new Date().getFullYear()} Phoenix Suite · Made in Melbourne, Australia
      </div>
    </footer>
  );
}

export function MarketingHome() {
  return (
    <div style={{ background: T.bg, color: T.text, minHeight: '100vh' }}>
      <Helmet>
        <title>Phoenix Suite — All-in-one gym + martial arts management software</title>
        <meta name="description" content="The Australian-made management platform for martial arts academies, gyms, and PT studios. Native STP payroll, Xero + QuickBooks sync, branded mobile app, class credits, and more." />
        <link rel="canonical" href="https://phoenixsuite.com.au" />
      </Helmet>
      <style>{globalCss}</style>
      <Header />

      {/* Hero */}
      <section style={{ padding: '100px 0 80px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -200, right: -200, width: 600, height: 600, background: 'radial-gradient(circle, rgba(91,108,240,.15), transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 20, background: 'rgba(34,211,238,.12)', color: T.accent, fontSize: 12, fontWeight: 600, letterSpacing: .5, marginBottom: 24 }}>
            AUSTRALIAN-BUILT · STP PHASE 2 READY · 14-DAY FREE TRIAL
          </div>
          <h1 className="hero-title" style={{ fontSize: 60, fontWeight: 800, lineHeight: 1.1, maxWidth: 880, margin: '0 auto 20px', letterSpacing: -1 }}>
            Run your studio on<br />
            <span style={{ background: `linear-gradient(135deg, ${T.pri}, ${T.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              one platform
            </span>
          </h1>
          <p className="hero-subtitle" style={{ fontSize: 19, color: T.muted, maxWidth: 640, margin: '0 auto 36px' }}>
            Phoenix Suite replaces 8+ tools with one platform — memberships, bookings, payroll,
            accounting sync, a branded mobile app, and everything a martial arts academy or gym needs.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://app.phoenixsuite.com.au/register" className="btn btn-pri">Start free 14-day trial →</a>
            <Link to="/features" className="btn btn-ghost">See features</Link>
          </div>
          <p style={{ fontSize: 12, color: T.dim, marginTop: 20 }}>No credit card required · Cancel anytime · Local support</p>
        </div>
      </section>

      {/* Social proof */}
      <section style={{ padding: '40px 0', borderTop: `1px solid ${T.brd}`, borderBottom: `1px solid ${T.brd}` }}>
        <div className="container">
          <p style={{ textAlign: 'center', fontSize: 12, color: T.dim, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 24 }}>
            Built for Australian studios — with everything Mindbody charges extra for
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 24, textAlign: 'center' }}>
            {[
              ['Native STP', 'Single Touch Payroll Phase 2 built in', false],
              ['Xero + QBO sync', 'Two-way accounting — no exports', false],
              ['BAS-ready GST', 'Automated monthly reports', false],
              ['Branded app', 'Your logo on the App Store', true],
            ].map(([t, s, imminent]) => (
              <div key={t}>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>
                  {t}{imminent && <ReleaseImminentBadge />}
                </div>
                <div style={{ fontSize: 12, color: T.muted }}>{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature highlight grid */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 36, fontWeight: 800, marginBottom: 14, letterSpacing: -.5 }}>Everything you need. Nothing you don't.</h2>
            <p style={{ fontSize: 16, color: T.muted, maxWidth: 620, margin: '0 auto' }}>
              Designed from the ground up for the Australian fitness and martial arts market.
              No US-first workarounds. No bolt-on payroll modules.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
            {FEATURES_SHORT.map((f) => (
              <div key={f.title} style={{ background: T.card, border: `1px solid ${T.brd}`, borderRadius: 14, padding: 24 }}>
                <div style={{ fontSize: 26, marginBottom: 12 }}>{f.icon}</div>
                <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>
                  {f.title}{f.imminent && <ReleaseImminentBadge />}
                </h3>
                <p style={{ fontSize: 14, color: T.muted, lineHeight: 1.55 }}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compare */}
      <section style={{ padding: '80px 0', background: T.card, borderTop: `1px solid ${T.brd}`, borderBottom: `1px solid ${T.brd}` }}>
        <div className="container">
          <h2 style={{ fontSize: 34, fontWeight: 800, textAlign: 'center', marginBottom: 10 }}>Where Phoenix Suite wins</h2>
          <p style={{ textAlign: 'center', color: T.muted, marginBottom: 48 }}>How we stack up against the incumbents</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '14px 16px', color: T.muted, fontWeight: 600 }}></th>
                  <th style={{ textAlign: 'center', padding: '14px 16px', color: T.pri, fontWeight: 700 }}>Phoenix Suite</th>
                  <th style={{ textAlign: 'center', padding: '14px 16px', color: T.muted, fontWeight: 600 }}>Mindbody</th>
                  <th style={{ textAlign: 'center', padding: '14px 16px', color: T.muted, fontWeight: 600 }}>Spark</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row) => (
                  <tr key={row[0]} style={{ borderTop: `1px solid ${T.brd}` }}>
                    <td style={{ padding: '14px 16px', color: T.text }}>
                      {row[0]}{row[4] && <ReleaseImminentBadge />}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>{row[1] ? <span style={{ color: T.ok, fontWeight: 700 }}>✓</span> : <span style={{ color: T.dim }}>—</span>}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>{row[2] ? <span style={{ color: T.ok }}>✓</span> : <span style={{ color: T.dim }}>—</span>}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>{row[3] ? <span style={{ color: T.ok }}>✓</span> : <span style={{ color: T.dim }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '100px 0', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ fontSize: 42, fontWeight: 800, marginBottom: 16, letterSpacing: -.5 }}>Give your studio 5 hours a week back.</h2>
          <p style={{ color: T.muted, fontSize: 17, marginBottom: 32, maxWidth: 520, margin: '0 auto 32px' }}>
            14-day free trial. Full feature access. No credit card.
          </p>
          <a href="https://app.phoenixsuite.com.au/register" className="btn btn-pri" style={{ padding: '18px 36px', fontSize: 17 }}>
            Start free trial →
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const FEATURES_SHORT = [
  { icon: '💳', title: 'Memberships & Stripe', body: 'Recurring billing, installment plans, freezes, referrals, promo codes, save-card auto-charge — all with your own Stripe account.' },
  { icon: '📅', title: 'Class scheduling', body: 'Recurring classes, waitlists with SMS fallback, virtual classes, class credit packages, instructor attendance, kiosk check-in.' },
  { icon: '📱', title: 'Native mobile app', body: 'iOS + Android. Member booking, check-in, workouts, nutrition tracking, heart-rate + scale BLE pairing, Apple Watch companion.', imminent: true },
  { icon: '🥋', title: 'Martial arts specific', body: 'Belt progression, grading events, multi-discipline instructors, practitioner portal, rank requirements tracking.' },
  { icon: '🏋️', title: 'Fitness / PT specific', body: 'Exercise library, program builder, workout broadcast to floor displays, live heart-rate leaderboard, macro + food logging.' },
  { icon: '🚪', title: 'Door access integration', body: 'Works with the top 10 AU access hardware vendors. Auto-revoke on overdue, auto-provision on sub active.' },
  { icon: '🤖', title: 'Marketing automation', body: 'Drip sequences, lead funnels, website builder with SEO, broadcast SMS/email, cohort + LTV + churn reports.' },
  { icon: '💼', title: 'Native payroll + STP', body: 'AU NAT 1004 PAYG tables, super guarantee, leave balances, STP Phase 2 pay events lodged via Ozedi or SuperChoice.' },
  { icon: '📊', title: 'Accounting sync', body: 'Two-way Xero + QuickBooks Online. Invoices, payments, contacts, voids — all synced automatically. MYOB coming soon.' },
];

// [label, phoenix, mindbody, spark, imminent?]
// imminent=true adds a "Release Imminent" badge next to the label for
// mobile-app-dependent rows.
const COMPARE_ROWS = [
  ['Native AU payroll + STP Phase 2', true, false, false, false],
  ['BAS-ready monthly GST report', true, false, false, false],
  ['Xero + QuickBooks two-way sync', true, true, false, false],
  ['Native iOS + Android app', true, true, true, true],
  ['Class credit packages', true, true, false, false],
  ['Installment plans', true, true, false, false],
  ['Martial arts belt progression', true, false, false, false],
  ['Door access hardware integration', true, false, false, false],
  ['Workout broadcast to floor displays', true, false, false, false],
  ['Apple Watch companion', true, false, false, true],
  ['BLE heart-rate + scale pairing', true, false, false, true],
  ['Cohort / LTV / churn analytics', true, true, false, false],
  ['Open REST API + per-org keys', true, true, false, false],
  ['Multi-location franchise support', true, true, false, false],
  ['Branded app on your App Store', true, false, false, true],
];

export function MarketingFeatures() {
  return (
    <div style={{ background: T.bg, color: T.text, minHeight: '100vh' }}>
      <Helmet>
        <title>Features — Phoenix Suite</title>
        <meta name="description" content="Full feature breakdown of Phoenix Suite: memberships, scheduling, native mobile app, STP payroll, Xero + QuickBooks sync, martial arts belt progression, branded apps and more." />
      </Helmet>
      <style>{globalCss}</style>
      <Header />

      <section style={{ padding: '80px 0 40px' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 48, fontWeight: 800, marginBottom: 14, letterSpacing: -.5 }}>Every feature you'd need.</h1>
          <p style={{ fontSize: 17, color: T.muted, maxWidth: 640, margin: '0 auto' }}>
            The complete list. No gotchas, no add-on modules.
          </p>
        </div>
      </section>

      <section style={{ padding: '40px 0 80px' }}>
        <div className="container">
          {FEATURE_GROUPS.map((group) => (
            <div key={group.title} style={{ marginBottom: 56 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                <span style={{ fontSize: 28 }}>{group.icon}</span>
                <h2 style={{ fontSize: 26, fontWeight: 800 }}>
                  {group.title}{group.imminent && <ReleaseImminentBadge />}
                </h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                {group.items.map((item) => (
                  <div key={item} style={{ padding: '12px 16px', background: T.card, borderRadius: 10, border: `1px solid ${T.brd}`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ color: T.ok, fontWeight: 700, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 14, color: T.text }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section style={{ padding: '60px 0', textAlign: 'center' }}>
        <a href="https://app.phoenixsuite.com.au/register" className="btn btn-pri" style={{ padding: '16px 32px', fontSize: 16 }}>
          Start your 14-day free trial →
        </a>
      </section>

      <Footer />
    </div>
  );
}

const FEATURE_GROUPS = [
  { icon: '💳', title: 'Memberships & payments', items: [
    'Recurring subscriptions (weekly / fortnightly / monthly / annual)',
    'Stripe integration with your own account',
    'Save card + auto-charge',
    'Installment plans',
    'Class credit packages',
    'Promo codes (% or $ off, scoped)',
    'Gift cards',
    'Referral rewards',
    'Freezes + holds with backdating',
    'Family groups with shared billing',
    'Account sales ledger',
    'Stripe Checkout + PaymentSheet mobile',
  ]},
  { icon: '📅', title: 'Scheduling', items: [
    'Recurring class templates',
    'Waitlists with auto-promote + SMS fallback',
    'Virtual classes with join URL windows',
    'Instructor attendance + timesheets',
    'Kiosk check-in with QR',
    'Self check-in via mobile app',
    'Late-cancel + no-show policies',
    'Capacity + equipment limits',
    'Discipline-scoped instructor permissions',
  ]},
  { icon: '📱', title: 'Mobile apps', imminent: true, items: [
    'Native iOS + Android (React Native / Expo)',
    'Apple Watch companion scaffold',
    'BLE heart-rate monitor pairing',
    'BLE weight scale pairing (Weight Scale 0x181D)',
    'Food barcode scanning via OpenFoodFacts',
    'Macro targets + food logging',
    'Workout player with step-by-step guidance',
    'Push notifications (Expo Push)',
    'Community feed + leaderboards',
    'Self-service freeze/hold from app',
  ]},
  { icon: '🥋', title: 'Martial arts', items: [
    'Belt/rank progression per discipline',
    'Grading events with candidate invites',
    'Rank requirement tracking',
    'Practitioner portal with temporary code login',
    'Discipline-specific instructor certifications',
    'Multi-discipline student enrolment',
  ]},
  { icon: '🏋️', title: 'Fitness & PT', items: [
    'Exercise library with video',
    'Program builder (strength, conditioning, therapy)',
    'Workout broadcast to floor displays',
    'Live heart-rate leaderboard',
    'BLE + Apple Health + Google Health integration',
    'Body measurements tracking',
    'Therapeutic programs via practitioner portal',
  ]},
  { icon: '💼', title: 'Payroll + STP', items: [
    'NAT 1004 Scale 1/2/3 PAYG withholding',
    'Super guarantee auto-calc',
    'HELP + STSL progressive tables',
    'Annual + sick + long-service leave tracking',
    'Pay run draft → finalise → lodge workflow',
    'STP Phase 2 payload generation',
    'Ozedi + SuperChoice direct lodgment',
    'Manual STP upload fallback',
    'Pay slip PDF export',
  ]},
  { icon: '📊', title: 'Accounting & reports', items: [
    'Two-way Xero sync (invoices + payments + contacts)',
    'Two-way QuickBooks Online sync',
    'Monthly BAS-ready GST report',
    'Auto-email monthly summary to admins',
    'Cohort retention reports',
    'LTV per plan',
    'Churn driver analysis',
    'Attendance + utilisation reports',
    'Shop + POS with GST-inclusive pricing',
    'Refunds with stock return',
    'Depreciation schedules',
  ]},
  { icon: '🚪', title: 'Access hardware', items: [
    'Salto KS',
    'Kisi',
    'Openpath',
    'Brivo',
    'Verkada',
    'Nayax',
    'GymMaster Access',
    'Gantner',
    'Net2 (Paxton)',
    'Pinforce',
    'Auto-revoke on overdue',
    'Auto-provision on subscription activation',
  ]},
  { icon: '📣', title: 'Marketing + website', items: [
    'Drip email / SMS sequences',
    'Lead capture forms',
    'Website builder per-org with SEO',
    'JSON-LD structured data for discovery',
    'Broadcast email + SMS',
    'Twilio number provisioning',
    'Trial lead pipeline tracking',
    'Public signup pages',
    'Custom domain support',
  ]},
  { icon: '🛡️', title: 'Admin & platform', items: [
    'Multi-tenant with per-org Stripe',
    'Modular licensing (MA + Fitness + Marketing separate)',
    'Org groups for multi-location franchises',
    'Per-org API keys with scoped auth',
    'Rate limiting per-org',
    'OpenAPI 3.0 spec',
    'Webhooks for external integrations',
    'Staff permissions with discipline scoping',
    'Audit log',
    'GDPR + Australian Privacy Act exports',
  ]},
  { icon: '🏢', title: 'White-label', imminent: true, items: [
    'Your branded app on the App Store',
    'Your branded app on Google Play',
    'Custom colours, icon, splash, scheme',
    'EAS build pipeline integrated',
    'Operator-owned Apple + Google accounts',
    'Custom deep link scheme',
  ]},
];

export function MarketingPricing() {
  return (
    <div style={{ background: T.bg, color: T.text, minHeight: '100vh' }}>
      <Helmet>
        <title>Pricing — Phoenix Suite</title>
        <meta name="description" content="Phoenix Suite pricing: 14-day free trial, then $99 AUD/month for base, +$49 per module. No per-member fees for the first 200 members. Cancel anytime." />
      </Helmet>
      <style>{globalCss}</style>
      <Header />

      <section style={{ padding: '80px 0 40px', textAlign: 'center' }}>
        <div className="container">
          <h1 style={{ fontSize: 48, fontWeight: 800, marginBottom: 14, letterSpacing: -.5 }}>Simple, transparent pricing</h1>
          <p style={{ fontSize: 17, color: T.muted, maxWidth: 620, margin: '0 auto' }}>
            Start with a 14-day free trial. Pay only for the modules you use. No per-member transaction fees.
          </p>
        </div>
      </section>

      <section style={{ padding: '20px 0 60px' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, maxWidth: 1080, margin: '0 auto' }}>
            {PRICING_TIERS.map((tier) => (
              <div key={tier.name} style={{
                background: tier.featured ? `linear-gradient(180deg, rgba(91,108,240,.08), ${T.card})` : T.card,
                border: `1px solid ${tier.featured ? T.pri : T.brd}`,
                borderRadius: 14, padding: 32, position: 'relative',
              }}>
                {tier.featured && (
                  <div style={{ position: 'absolute', top: -12, right: 20, background: T.pri, color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, letterSpacing: .5 }}>
                    RECOMMENDED
                  </div>
                )}
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{tier.name}</h2>
                <p style={{ color: T.muted, fontSize: 13, marginBottom: 20, minHeight: 40 }}>{tier.tagline}</p>
                <div style={{ marginBottom: 20 }}>
                  <span style={{ fontSize: 42, fontWeight: 800, letterSpacing: -1 }}>${tier.price}</span>
                  <span style={{ color: T.muted, fontSize: 14, marginLeft: 6 }}>AUD / {tier.period}</span>
                </div>
                <a href={`https://app.phoenixsuite.com.au/register?plan=${tier.slug}`}
                   className={tier.featured ? 'btn btn-pri' : 'btn btn-ghost'}
                   style={{ width: '100%', justifyContent: 'center', marginBottom: 24 }}>
                  {tier.cta}
                </a>
                <div style={{ borderTop: `1px solid ${T.brd}`, paddingTop: 20 }}>
                  {tier.features.map((f) => {
                    const feature = typeof f === 'string' ? { label: f } : f;
                    return (
                      <div key={feature.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10, fontSize: 13 }}>
                        <span style={{ color: T.ok, fontWeight: 700 }}>✓</span>
                        <span style={{ color: T.text }}>
                          {feature.label}
                          {feature.imminent && <ReleaseImminentBadge />}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '60px 0', background: T.card, borderTop: `1px solid ${T.brd}`, borderBottom: `1px solid ${T.brd}` }}>
        <div className="container">
          <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 28, textAlign: 'center' }}>Frequently asked</h2>
          <div style={{ maxWidth: 780, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {FAQ.map((q) => (
              <div key={q.q} style={{ padding: '18px 20px', background: T.bg, border: `1px solid ${T.brd}`, borderRadius: 10 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{q.q}</h3>
                <p style={{ fontSize: 14, color: T.muted }}>{q.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const PRICING_TIERS = [
  {
    slug: 'starter',
    name: 'Starter',
    tagline: 'For solo PTs and small studios getting off the ground',
    price: 99,
    period: 'month',
    cta: 'Start free trial',
    featured: false,
    features: [
      'Up to 100 active members',
      'Memberships + Stripe billing',
      'Class scheduling + kiosk check-in',
      { label: 'Native mobile app (shared branding)', imminent: true },
      'BAS-ready monthly GST report',
      'Xero + QuickBooks sync',
      'Email support',
    ],
  },
  {
    slug: 'studio',
    name: 'Studio',
    tagline: 'For established studios — everything you need to scale',
    price: 197,
    period: 'month',
    cta: 'Start free trial',
    featured: true,
    features: [
      'Up to 500 active members',
      'Everything in Starter',
      'Martial arts OR Fitness module',
      'Native STP Phase 2 payroll',
      'Door access integration',
      'Marketing automation + website builder',
      { label: 'Workout broadcast + BLE integration', imminent: true },
      'Cohort + LTV + churn reports',
      'Priority support',
    ],
  },
  {
    slug: 'enterprise',
    name: 'Enterprise',
    tagline: 'For franchises, multi-location, and white-label operators',
    price: 397,
    period: 'month',
    cta: 'Start free trial',
    featured: false,
    features: [
      'Unlimited members',
      'Everything in Studio',
      'Both MA + Fitness modules included',
      'Multi-location / org groups',
      { label: 'Your branded app on the App Store', imminent: true },
      'Open API with per-org keys',
      'Custom domain for member portal',
      'Dedicated onboarding',
      '24×7 phone support',
    ],
  },
];

const FAQ = [
  { q: 'How does the free trial work?', a: '14 days, full access to every feature including native payroll and STP. No credit card required. If you don\'t pick a plan at the end, your data stays safe for 60 days.' },
  { q: 'Do you charge per-member or transaction fees?', a: 'No. Your plan covers everything up to its member cap. Stripe fees go directly to your own Stripe account — we don\'t take a cut.' },
  { q: 'Can I bring my own Stripe / Twilio / Xero?', a: 'Yes — and we recommend it. You connect your own Stripe for member billing, your own Twilio for SMS, and your own Xero or QuickBooks. Phoenix never holds your customers\' funds.' },
  { q: 'Is payroll really included?', a: 'Yes. Full NAT 1004 PAYG withholding, super guarantee, leave tracking, and STP Phase 2 payload generation. Lodge via Ozedi or SuperChoice (your API credentials), or download the JSON to upload manually.' },
  { q: 'What happens if I go over my member cap?', a: 'We\'ll email you the month before you hit it. Upgrade anytime — we pro-rate the difference. No surprise bills.' },
  { q: 'Can I cancel anytime?', a: 'Yes. Month-to-month, no contracts. Export all your data as CSV before you leave.' },
  { q: 'Is support Australian?', a: 'Yes — Melbourne-based team, AEST business hours. Enterprise includes 24×7 phone.' },
];
